import { useState } from 'react';
import {
	add,
	NorthlightRenewals,
	NorthlightRenewalsSchema,
} from '@/api/northlight-renewals';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

export function AddNorthlightRenewals() {
	const queryClient = useQueryClient();
	const [formError, setFormError] = useState<string | null>(null);
	const navigate = useNavigate();
	const billToOptions = [
		{ text: 'Insured', value: 'insured' },
		{ text: 'Mortgagee', value: 'mortgagee' },
	];

	const form = useForm<NorthlightRenewals>({
		resolver: zodResolver(NorthlightRenewalsSchema),
		defaultValues: {
			id: null,
			policyNumber: '',
			policyEffectiveDate: '',
			renewalTerms: '',
			agentName: '',
			agentLastName: '',
			agentEmail: '',
			file: undefined as unknown as File,
			billToList: [],
			billto: '',
		},
		mode: 'all',
	});
	const { mutate, isPending } = useMutation({
		mutationFn: add,
		onSuccess: (data) => {
			setFormError(null);
			toast.success(`Invoice review submitted successfully.`, {
				dismissible: true,
			});

			queryClient.setQueryData(['formResponse'], data);
			form.reset();
			navigate('/northlight-renewal/review');
		},
		onError: (error: unknown) => {
			const errorMessage =
				error instanceof Error ? error.message : 'An unexpected error occurred';
			setFormError(errorMessage);
			queryClient.setQueryData(['formResponse'], errorMessage);
		},
	});

	const onSubmit: SubmitHandler<NorthlightRenewals> = (values) => {
		mutate(values);
	};

	return (
		<>
			<div className="max-w-d mx-auto px-4">
				<h1 className="mb-4 text-center text-2xl font-semibold">
					NorthLight Renewals
				</h1>
				{formError && (
					<div className="mx-auto max-w-md px-4 py-2 text-center text-red-700">
						{formError}
					</div>
				)}
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="mx-auto max-w-md space-y-6"
					>
						<FormField
							control={form.control}
							name="policyNumber"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Policy Number</FormLabel>
									<FormControl>
										<Input {...field} placeholder="Enter Policy Number" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="policyEffectiveDate"
							render={({ field }) => (
								<FormItem className="flex flex-col">
									<FormLabel>Policy Effective Date</FormLabel>

									<FormControl>
										<Input
											type="date"
											value={
												field.value ? format(new Date(field.value), 'yyyy-MM-dd') : ''
											}
											onChange={(e) => {
												const value = e.target.value;
												if (value) {
													const inputDate = new Date(value + 'T00:00:00');
													field.onChange(inputDate.toISOString());
												}
											}}
										/>
									</FormControl>

									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="file"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Upload File</FormLabel>
									<FormControl>
										<Input
											type="file"
											accept="application/pdf"
											onChange={(e) => field.onChange(e.target.files?.[0] ?? null)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="billToList"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Bill To</FormLabel>
									<FormControl>
										<Select
											onValueChange={(val) => {
												field.onChange([{ text: val, value: val }]);
												form.setValue('billto', val);
											}}
											value={field.value?.[0]?.value ?? ''}
										>
											<SelectTrigger>
												<SelectValue placeholder="--- Select Bill To ---" />
											</SelectTrigger>
											<SelectContent>
												{billToOptions.map((opt) => (
													<SelectItem key={opt.value} value={opt.value}>
														{opt.text}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							name="renewalTerms"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Renewal Terms</FormLabel>
									<FormControl>
										<textarea
											{...field}
											className="w-full rounded-md border p-2"
											rows={5}
											placeholder="Enter renewal terms..."
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							name="agentName"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Agent First Name</FormLabel>
									<FormControl>
										<Input {...field} placeholder="First Name" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							name="agentLastName"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Agent Last Name</FormLabel>
									<FormControl>
										<Input {...field} placeholder="Last Name" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							name="agentEmail"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Agent Email</FormLabel>
									<FormControl>
										<Input {...field} type="email" placeholder="example@email.com" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<div className="flex gap-3">
							<Button
								type="submit"
								className="bg-red-600 text-white hover:bg-red-700"
								disabled={isPending}
							>
								{isPending ? 'Submitting...' : 'Review Invoice'}
							</Button>
							<Button type="button" variant="outline" onClick={() => form.reset()}>
								Cancel
							</Button>
						</div>
					</form>
				</Form>
			</div>
		</>
	);
}
